/* @flow */

import React, { useContext, useState, useEffect, useLayoutEffect } from "react";
import cn from "classnames";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { Link, NavLink } from "react-router-dom";
import { useTranslate } from "@awardit/react-use-translate";
import { StoreInfoContext } from "entrypoint/shared";
import { QuoteData } from "data";
import { useData } from "crustate/react";
import Wrapper from "components/Wrapper";
import Button from "components/Button";
import CartCounter from "components/AppHeader/CartCounter";
import useSessionStorage from "helpers/use-session-storage";
import SystemMessages from "components/SystemMessages";
import MobileMenu from "../MobileMenu";
import SearchMenu from "../SearchMenu";
import Drawer from "../Drawer";
import Logo from "../logo.svg";
import HamburgerIcon from "icons/hamburger.svg";
import CustomerServiceIcon from "icons/customer-service.svg";
import SearchIcon from "icons/search.svg";

import { CartMini } from "@crossroads/ui-components";

import styles from "./styles.scss";
import { useSubNavOpen } from "context/sub-nav-open";
import { useViewMode } from "context/view-mode";

type Props = {
  className: string,
  hamburgerOpen: boolean,
  setHamburgerOpen: boolean => void,
  onCheckout: boolean,
  useBannerStyling: boolean,
};

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const AppHeaderSmall = ({
  className = "",
  hamburgerOpen,
  setHamburgerOpen,
  onCheckout,
  useBannerStyling,
}: Props): React$Node => {
  const [isOpening, setIsOpening] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isolationMode] = useSessionStorage("navIsolationMode", "false");
  const navIsolationMode = isolationMode === "true";
  const drawerNode = React.createRef();
  const offCanvasNode = React.createRef();
  const [subNavOpen, setSubNavOpen] = useSubNavOpen();
  const [viewMode, setViewMode] = useViewMode();

  const showHeader = true;

  const openHamburger = () => {
    setIsOpening(true);
    setHamburgerOpen(true);

    setTimeout(() => {
      setIsOpening(false);
    }, Number.parseInt(styles.animationDuration, 10));
  };

  const t = useTranslate();

  const closeHamburger = () => {
    setIsClosing(true);

    setTimeout(() => {
      setSubNavOpen(false);
      setHamburgerOpen(false);
      setIsClosing(false);
      setViewMode("normal");
    }, Number.parseInt(styles.animationDuration, 10));
  };

  useEffect(() => {
    if (viewMode === "normal" || viewMode === "cart") {
      setHamburgerOpen(false);
    }
  }, [viewMode]);

  useIsomorphicLayoutEffect(() => {
    if (!hamburgerOpen) {
      enableBodyScroll(offCanvasNode.current);
      enableBodyScroll(drawerNode.current);
    }
    else {
      disableBodyScroll(offCanvasNode.current);
      disableBodyScroll(drawerNode.current);
    }
  }, [hamburgerOpen]);

  const { categories } = useContext(StoreInfoContext);
  const quoteData = useData(QuoteData);

  const toggleCart = () => setViewMode(viewMode === "cart" ? "normal" : "cart");

  return (
    <div className={styles.outerContainer}>
      <div className={cn(
        styles.container,
        styles["mode__" + viewMode],
        { [styles.hamburgerOpen]: hamburgerOpen },
        { [styles.isOpening]: isOpening },
        { [styles.isClosing]: isClosing },
        { [styles.subNavOpen]: subNavOpen },
        { [styles.addHeight]: useBannerStyling }
      )}
      >
        <header className={cn(styles.block, className)}>
          <Wrapper className={cn(styles.wrapper)}>
            <div className={styles.left}>
              {navIsolationMode ? (
                <div className={styles.padding}>
                  <Logo
                    alt={t("LOGO.ALT")}
                    aria-label={t("LOGO.TITLE")}
                    className={styles.logo}
                  />
                </div>
              ) : (
                <Link className={cn(styles.item, styles.padding, styles.itemLogo)} to="/">
                  <Logo
                    alt={t("LOGO.ALT")}
                    aria-label={t("LOGO.TITLE")}
                    className={styles.logo}
                  />
                </Link>
              )}
            </div>

            <div className={styles.right}>
              {!hamburgerOpen &&
              <nav aria-label={t("HEADER.MAIN_NAVIGATION")} className={styles.nav}>
                {!onCheckout &&
                  <NavLink
                    className={cn(styles.item, styles.padding)}
                    activeClassName={styles.active}
                    aria-label={t("HEADER.HELP")}
                    to="/faq"
                  >
                    <CustomerServiceIcon aria-label={t("ICONS.HEADPHONES")} />
                  </NavLink>
                }

                {!onCheckout && !navIsolationMode &&
                  <Button
                    aria-expanded={viewMode === "search" ?? "true"}
                    aria-controls="searchMenuMobile"
                    aria-label={viewMode === "search" ? t("HEADER.CLOSE_SEARCH") : t("HEADER.OPEN_SEARCH")}
                    className={cn(
                      styles.item,
                      { [styles.active]: viewMode === "search" },
                      styles.padding
                    )}
                    onClick={() => {
                      setViewMode(viewMode === "search" ? "normal" : "search");
                    }}
                  >
                    <SearchIcon aria-label={t("ICONS.MAGNIFIER")} />
                  </Button>
                }

                {viewMode === "search" &&
                  <div id="searchMenuMobile" role="search" aria-label={t("HEADER.SEARCH")} className={cn(styles.search, { [styles.addHeight]: useBannerStyling })}>
                    <SearchMenu
                      heading={t("HEADER.SEARCH")}
                      onClose={closeHamburger}
                      onBack={() => setSubNavOpen(false)}
                    />
                  </div>
                }

                <CartCounter className={cn(styles.item, styles.padding)} />

                {!navIsolationMode && !onCheckout &&
                  <Button
                    className={cn(styles.item, styles.padding)}
                    aria-controls="mobileMenu"
                    aria-label={t("HEADER.OPEN_MENU")}
                    aria-expanded={hamburgerOpen ?? "true"}
                    onClick={() => hamburgerOpen ? closeHamburger() : openHamburger()}
                  >
                    <HamburgerIcon aria-label={t("ICONS.HAMBURGER")} />
                  </Button>
                }
              </nav>
              }
            </div>
          </Wrapper>
        </header>

        <MobileMenu
          hamburgerOpen={hamburgerOpen}
          offCanvasNode={offCanvasNode}
          setViewMode={setViewMode}
          viewMode={viewMode}
          setSubNavOpen={setSubNavOpen}
          subNavOpen={subNavOpen}
          setHamburgerOpen={setHamburgerOpen}
          closeHamburger={closeHamburger}
        />

        {(hamburgerOpen || isClosing) && subNavOpen && (
          <nav
            id="categoriesMobile"
            aria-label={t("HEADER.CATEGORIES")}
            className={styles.categories}
          >
            <Drawer
              ref={drawerNode}
              useFocus
              setHamburgerOpen={setHamburgerOpen}
              heading={t("HEADER.CATEGORIES")}
              closeText={t("HEADER.CLOSE_CATEGORIES")}
              items={categories}
              onClose={closeHamburger}
              onItemClick={() => setViewMode("normal")}
              onBack={() => setSubNavOpen(false)}
            />
          </nav>
        )}
        {viewMode === "cart" &&
          <Wrapper>
            <CartMini
              ctaUrl={t("URL.CHECKOUT_CART")}
              variant="zupergift"
              showHeader={showHeader}
              quoteData={quoteData}
              onClose={toggleCart}
            />
          </Wrapper>
        }
      </div>

      {((hamburgerOpen || isClosing) || viewMode === "cart") &&
        <div
          className={cn(styles.dim, { [styles.isClosing]: isClosing })} onClick={() => {
            setViewMode("normal");
            closeHamburger();
          }} />
      }

      {((hamburgerOpen || isClosing) || viewMode === "search") &&
        <div
          className={cn(
            styles.dimSearch,
            {
              [styles.isClosing]: isClosing,
              [styles.bannerStyling]: useBannerStyling,
            })
          }
          onClick={() => {
            setViewMode("normal");
            closeHamburger();
          }}
        />
      }

      <Wrapper>
        <SystemMessages />
      </Wrapper>
    </div>
  );
};

export default AppHeaderSmall;
